/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import * as user_directory from '@fof-user-directory';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import Button from 'flarum/common/components/Button';
import type ItemList from 'flarum/common/utils/ItemList';
import type Mithril from 'mithril';

/**
 * Search parameters tracked by `fof/user-directory`'s page state.
 */
export interface UserDirectoryParams {
	sort?: string;
}

/**
 * The slice of `fof/user-directory`'s `UserDirectoryState` used here.
 */
export interface UserDirectoryStateLike {
	sortMap(): Record<string, string>;
	getParams(): UserDirectoryParams;
}

/**
 * The slice of `fof/user-directory`'s `UserDirectoryPage` used here.
 */
export type UserDirectoryPageLike = {
	state: UserDirectoryStateLike;
	changeParams(sort: string): void;
	sidebarItems(): ItemList<Mithril.Children>;
};

export default function () {
	if (app.initializers.has('fof-user-directory')) {
		extend(
			user_directory.UserDirectoryPage.prototype as UserDirectoryPageLike,
			'sidebarItems',
			function (this: UserDirectoryPageLike, items: ItemList<Mithril.Children>) {
				// Empty the item set: we will add only new elements and don't want any old ones
				items.toArray().forEach(({ itemName }) => items.remove(itemName));

				items.add('title', <h3>{app.translator.trans('glowingblue-orell-thalia-common.forum.user_directory.sort')}</h3>, 150);

				const sortMap = this.state.sortMap();

				const sortOptions: Mithril.Children[] = [];
				for (const key in sortMap) {
					sortOptions.push(
						<Button
							onclick={() => this.changeParams(key)}
							className={`Button Button--flat ${
								this.state.getParams().sort === key ||
								(!this.state.getParams().sort && key === app.forum.attribute<string>('userDirectoryDefaultSort'))
									? 'active'
									: ''
							}`}
						>
							{app.translator.trans(`fof-user-directory.lib.sort.${key}`)}
						</Button>
					);
				}

				items.add(
					'sort',
					<SelectDropdown buttonClassName="Button" className="App-titleControl">
						{sortOptions}
					</SelectDropdown>
				);
			}
		);
	}
}
